import {
  App,
  Button,
  Card,
  Col,
  Input,
  message,
  Pagination,
  Row,
  Select,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import GameBox from "../../components/GameBox";
import { useNavigate } from "react-router-dom";
import { LIST_GAME, GAME_STATUS, DELETE_GAME } from "../../apis/apis";
import debounce from "lodash.debounce";
import CulsightPageLoader from "../../components/CulsightPageLoader";

function Games() {
  const Navigate = useNavigate();
  const { notification } = App.useApp();

  const [games, set_games] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination_loader, set_pagination_loader] = useState(false);
  const [current_page, set_current_page] = useState("");
  const [total_pages, set_total_pages] = useState("");
  const [total_games, set_total_games] = useState(0);

  const [search_field, set_search_field] = useState("title");
  const [search_query, set_search_query] = useState("");

  const [isPublished, setIsPublished] = useState({});
  const [page_size, set_page_size] = useState(9);
  const [emptyMessage, setEmptyMessage] = useState("");

  // Fetch Games
  const fetchGames = async (
    page = current_page || 1,
    size = page_size,
    searchValue = search_query,
    field = search_field,
  ) => {
    setLoading(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("per_page", size);
    FORM_DATA.append("page", page);

    if (field === "title") FORM_DATA.append("title", searchValue);
    else FORM_DATA.append("tag", searchValue);

    const API_CALL = await LIST_GAME(FORM_DATA);

    if (API_CALL?.data?.status) {
      set_games(API_CALL.data.data || []);
      set_total_games(API_CALL.data.total_games || 0);
      set_current_page(API_CALL.data.current_page || 1);
      set_total_pages(API_CALL.data.total_pages || 0);

      setEmptyMessage(API_CALL.data.message || "");
    } else {
      set_games([]);
      set_total_games(0);
      set_total_pages(0);

      setEmptyMessage(API_CALL?.data?.message);
    }

    setLoading(false);
    set_pagination_loader(false);
  };

  useEffect(() => {
    fetchGames(1, page_size);
  }, [page_size]);

  // Pagination
  const pagination_on_change = async (page, size) => {
    set_pagination_loader(true);
    fetchGames(page, size);
  };

  // Search
  const debouncedFetchResults = useCallback(
    debounce(async (value, field, size) => {
      set_pagination_loader(true);
      fetchGames(1, size, value, field);
    }, 500),
    [current_page, page_size, search_query, search_field],
  );

  const handleInput = (e) => {
    const value = e.target.value;
    set_search_query(value);
    debouncedFetchResults(value, search_field, page_size);
  };

  const handleSelectChange = (value) => {
    set_search_field(value);
    set_search_query("");
  };

  const selectBefore = (
    <Select value={search_field} onChange={handleSelectChange}>
      <Select.Option value="title">Title</Select.Option>
      <Select.Option value="tag">Tag</Select.Option>
    </Select>
  );

  // Publish Game
  const publishGame = async (game_id) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("id", game_id);

    try {
      const response = await GAME_STATUS(FORM_DATA);

      if (response?.status) {
        message.success("Game status updated successfully!");

        setIsPublished((prev) => ({
          ...prev,
          [game_id]: true,
        }));

        await fetchGames(current_page, page_size);
      } else {
        message.error(response?.message || "Failed to update game");
      }
    } catch (error) {
      console.error(error);
      message.error("Something went wrong");
    }
  };

  // Delete Game
  const deleteGame = async (game_id) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("id", game_id);

    try {
      const response = await DELETE_GAME(FORM_DATA);

      if (response?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });

        await fetchGames(current_page, page_size);
      } else {
        message.error(response?.message || "Failed to delete game");
      }
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    }
  };

  return (
    <div className="lms-body">
      <Card>
        <h2>
          Games
          <Button
            type="primary"
            size="large"
            onClick={() => Navigate("/add-game")}
            style={{ marginLeft: "10px", float: "right" }}
          >
            Add Game
          </Button>
        </h2>

        <br />

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={18} lg={12}>
            <Input
              addonBefore={selectBefore}
              placeholder={
                search_field === "title" ? "Search by title" : "Search by tag"
              }
              value={search_query}
              onChange={handleInput}
              size="large"
              style={{ width: "100%" }}
            />
          </Col>
        </Row>

        {loading ? (
          <CulsightPageLoader />
        ) : (
          <>
            <div style={{ marginTop: "20px" }}>
              <Row gutter={[30, 40]}>
                {games?.length > 0 ? (
                  games.map((items) => (
                    <Col lg={8} md={8} sm={12} xs={24} key={items.id}>
                      <GameBox
                        id={btoa(items.id)}
                        game_title={items.title}
                        game_image={items.thumbnail}
                        game_status={items.status}
                        game_tag_line={items.game_tag_line}
                        accessible_through={items.accessible_through}
                        users_count={items.users_count}
                        updated_at={items.updated_at}
                        publishGame={() => publishGame(items.id)}
                        isPublished={isPublished[items.id] === true}
                        deleteGame={() => deleteGame(items.id)}
                        editGame={() => Navigate(`/edit-game/${btoa(items.id)}`)}
                      />
                    </Col>
                  ))
                ) : (
                  <Col span={24}>
                    <p
                      style={{
                        textAlign: "center",
                        fontSize: "20px",
                        color: "red",
                        marginTop: "15px",
                      }}
                    ></p>
                  </Col>
                )}
              </Row>
            </div>
            {total_pages > 0 ? (
              <div style={{ float: "right", marginTop: "20px" }}>
                <Pagination
                  current={current_page}
                  total={total_games}
                  pageSize={page_size}
                  showSizeChanger
                  pageSizeOptions={["9", "18", "36", "71", "90"]}
                  onChange={pagination_on_change}
                  onShowSizeChange={(current, size) => {
                    set_page_size(size);
                    pagination_on_change(1, size);
                  }}
                  style={{ display: "inline-block" }}
                  className="no-search-pagination"
                />

                <style>
                  {`
                    .no-search-pagination .ant-select-selection-search-input {
                      display: none !important;
                    }
                  `}
                </style>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "red" }}>
                <h2>{emptyMessage}</h2>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default Games;
